import React, { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import {
  getProveedores,
  deleteProveedor,
  getProveedorById,
} from "../helpers/rutaProveedores";
import ModalProveedor from "./ModalProveedor";
import "../css/tableproveedores.css";

const COLS = {
  acciones: 100,
  nro: 70,
  razon: 220,
  domicilio: 180,
  telefono: 140,
  cuit: 140,
  email: 220,
  localidad: 180,
};

const cellTruncate = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const normalizarProveedores = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.proveedores)) return data.proveedores;
  if (Array.isArray(data?.data?.proveedores)) return data.data.proveedores;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getLocalidadTexto = (localidad) => {
  if (!localidad) return "-";
  if (typeof localidad === "string") return localidad;
  return localidad.localidad || localidad.nombre || "-";
};

const TableProveedores = () => {
  const [state, setState] = useState({
    proveedores: [],
    loading: true,
    error: "",
  });

  const [proveedor, setProveedor] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    consultaProveedores();
  }, []);

  const consultaProveedores = async () => {
    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      const datos = await getProveedores();

      const list = normalizarProveedores(datos)
        .filter((p) => p?.activo ?? true)
        .sort((a, b) =>
          String(a?.razonsocial || "").localeCompare(
            String(b?.razonsocial || ""),
            "es",
            { sensitivity: "base" }
          )
        );

      setState({
        proveedores: list,
        loading: false,
        error: "",
      });
    } catch (e) {
      setState({
        proveedores: [],
        loading: false,
        error: e?.message || "Error al cargar proveedores",
      });

      console.error("❌ getProveedores:", e);
    }
  };

  const handleClose = () => {
    setShow(false);
    setProveedor(null);
    consultaProveedores();
  };

  const modificaProveedor = async (id) => {
    try {
      const resp = await getProveedorById(id);

      const prov =
        resp?.proveedor ||
        resp?.proveedores ||
        resp?.data?.proveedor ||
        resp?.data?.proveedores ||
        resp ||
        null;

      if (!prov?._id) {
        console.error("Respuesta inesperada getProveedorById:", resp);
        alert("No se pudo abrir el proveedor.");
        return;
      }

      setProveedor(prov);
      setShow(true);
    } catch (e) {
      console.error("❌ getProveedorById:", e);
      alert("No se pudo abrir el proveedor.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Está seguro que desea borrar el proveedor?")) return;

    try {
      const resp = await deleteProveedor(id);

      if (resp?.ok === false) {
        const msg =
          resp?.err?.message ||
          resp?.error?.err?.message ||
          resp?.error?.message ||
          resp?.error ||
          "No se pudo borrar el proveedor.";

        alert(typeof msg === "string" ? msg : "No se pudo borrar el proveedor.");
        return;
      }

      consultaProveedores();
    } catch (e) {
      console.error("❌ deleteProveedor:", e);
      alert("No se pudo borrar el proveedor.");
    }
  };

  if (state.loading) {
    return <div className="p-3">Cargando proveedores…</div>;
  }

  if (state.error) {
    return <div className="p-3 text-danger">{state.error}</div>;
  }

  const rows = Array.isArray(state.proveedores) ? state.proveedores : [];

  return (
    <div className="table-container">
      <div className="col-12 mt-4" />

      <div>
        <Table
          striped
          bordered
          hover
          className="table-container"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead>
            <tr className="header">
              <th style={{ width: COLS.acciones }}>Modif/Elim</th>
              <th style={{ width: COLS.nro }}>Nro</th>
              <th style={{ width: COLS.razon }}>Razón Social</th>
              <th style={{ width: COLS.domicilio }}>Domicilio</th>
              <th style={{ width: COLS.telefono }}>Teléfono</th>
              <th style={{ width: COLS.cuit }}>CUIT</th>
              <th style={{ width: COLS.email }}>Email</th>
              <th style={{ width: COLS.localidad }}>Localidad</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center">
                  No hay proveedores.
                </td>
              </tr>
            ) : (
              rows.map((p) => (
                <tr key={p._id}>
                  <td style={{ width: COLS.acciones, whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "nowrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => modificaProveedor(p._id)}
                        title="Modificar"
                      >
                        <i
                          className="fa fa-pencil-square-o"
                          aria-hidden="true"
                        />
                      </button>

                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => onDelete(p._id)}
                        title="Eliminar"
                      >
                        <i className="fa fa-trash-o" aria-hidden="true" />
                      </button>
                    </div>
                  </td>

                  <td style={{ width: COLS.nro }}>{p.codprov}</td>

                  <td
                    style={{
                      width: COLS.razon,
                      textAlign: "left",
                      ...cellTruncate,
                    }}
                    title={p.razonsocial || ""}
                  >
                    {p.razonsocial || "-"}
                  </td>

                  <td
                    style={{
                      width: COLS.domicilio,
                      textAlign: "left",
                      ...cellTruncate,
                    }}
                    title={p.domicilio || ""}
                  >
                    {p.domicilio || "-"}
                  </td>

                  <td style={{ width: COLS.telefono }}>
                    {p.telefono || "-"}
                  </td>

                  <td style={{ width: COLS.cuit }}>{p.cuit || "-"}</td>

                  <td
                    style={{ width: COLS.email, ...cellTruncate }}
                    title={p.email || ""}
                  >
                    {p.email || "-"}
                  </td>

                  <td
                    style={{ width: COLS.localidad, ...cellTruncate }}
                    title={getLocalidadTexto(p.localidad)}
                  >
                    {getLocalidadTexto(p.localidad)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {show && proveedor && (
        <ModalProveedor
          show={show}
          handleClose={handleClose}
          proveedor={proveedor}
        />
      )}
    </div>
  );
};

export default TableProveedores;